import { useCallback, useMemo, useState } from 'react';
import {
  DEFAULT_GRAPH_SETTINGS,
  sanitizeSettings,
  type GraphSettings,
} from './graphModel';

/** localStorage key of a workspace's graph settings. */
export const graphSettingsKey = (wsName: string): string =>
  `sariz-notes:graph:${wsName}`;

function readRaw(wsName: string): unknown {
  if (typeof localStorage === 'undefined') {
    return DEFAULT_GRAPH_SETTINGS;
  }
  try {
    const raw = localStorage.getItem(graphSettingsKey(wsName));
    return raw ? JSON.parse(raw) : DEFAULT_GRAPH_SETTINGS;
  } catch {
    return DEFAULT_GRAPH_SETTINGS;
  }
}

function writeRaw(wsName: string, value: unknown): void {
  if (typeof localStorage === 'undefined') {
    return;
  }
  try {
    localStorage.setItem(graphSettingsKey(wsName), JSON.stringify(value));
  } catch {
    // Quota / private mode — settings stay in-memory for the session.
  }
}

/** Workspace graph settings, remembered per workspace in this browser. */
export function useGraphSettings(
  wsName: string,
): [GraphSettings, (patch: Partial<GraphSettings> | 'reset') => void] {
  const [raw, setRaw] = useState<unknown>(() => readRaw(wsName));
  // Re-read when the workspace changes.
  const [activeWs, setActiveWs] = useState(wsName);
  if (activeWs !== wsName) {
    setActiveWs(wsName);
    setRaw(readRaw(wsName));
  }

  const settings = useMemo(() => sanitizeSettings(raw), [raw]);
  const update = useCallback(
    (patch: Partial<GraphSettings> | 'reset') => {
      setRaw((prev: unknown) => {
        const next =
          patch === 'reset'
            ? DEFAULT_GRAPH_SETTINGS
            : { ...sanitizeSettings(prev), ...patch };
        writeRaw(wsName, next);
        return next;
      });
    },
    [wsName],
  );
  return [settings, update];
}
